'use client';

import posthogLib from 'posthog-js';
import { PostHogProvider as PHProvider } from 'posthog-js/react';
import type { CaptureResult } from 'posthog-js';
import { useEffect } from 'react';
import { redactEventProperties } from './redactUrl';

// Export posthog instance for direct usage
export const posthog = posthogLib;

/**
 * Hosts whose JavaScript we neither wrote nor can fix. Their unhandled throws
 * were the second-largest error class on the event page (~40 people in 7 days,
 * all from embed.tawk.to), which buried our own exceptions.
 */
const THIRD_PARTY_ERROR_HOSTS = ['embed.tawk.to', 'va.tawk.to', 'secure.wayforpay.com'];

export function isThirdPartyException(event: { event?: string; properties?: Record<string, unknown> } | null): boolean {
  if (!event) return false;
  if (event.event !== '$exception') return false;
  const list = event.properties?.$exception_list;
  if (!Array.isArray(list)) return false;

  return list.some((entry) => {
    const frames = (entry as { stacktrace?: { frames?: unknown[] } })?.stacktrace?.frames;
    if (!Array.isArray(frames)) return false;
    return frames.some((frame) => {
      const filename = (frame as { filename?: string })?.filename;
      return typeof filename === 'string' && THIRD_PARTY_ERROR_HOSTS.some((h) => filename.includes(h));
    });
  });
}

// Extracted (rather than inline in the init options below) so it can be
// exercised directly in tests without reaching into posthog-js internals.
export function beforeSend(event: CaptureResult | null): CaptureResult | null {
  return event && isThirdPartyException(event) ? null : event;
}

// Initialize PostHog with reverse proxy
// TODO: old env vars NEXT_PUBLIC_POSTHOG_KEY and NEXT_PUBLIC_POSTHOG_HOST still exist on Vercel – remove when convenient
if (typeof window !== 'undefined') {
  posthogLib.init('phc_Hx7TTunXfoM1BHlWtVZo57TVZaqZNritL9w6m98Ygfh', {
    api_host: 'https://t.confy.cc',
    person_profiles: 'identified_only',
    capture_pageview: false, // We'll capture pageviews manually
    capture_pageleave: true,
    // Bearer tokens ride in the URL on the invite and share pages, and
    // $current_url is attached to every event automatically. Redact centrally
    // here so no individual capture site has to remember. See redactUrl.ts.
    sanitize_properties: (properties) => redactEventProperties(properties),
    // Drop unhandled throws from third-party widgets before they crowd out our
    // own exceptions in the error list. See THIRD_PARTY_ERROR_HOSTS above.
    before_send: beforeSend,
  });
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    // Only track pageviews if PostHog is initialized
    if (posthogLib.__loaded) {
      posthogLib.capture('$pageview');
    }
  }, []);

  // Return children directly if PostHog is not configured
  if (!posthogLib.__loaded) {
    return <>{children}</>;
  }

  return <PHProvider client={posthogLib}>{children}</PHProvider>;
}
